import React, { useState } from "react";
import PropTypes from "prop-types";

/**
 * 左侧组件库：列出 schema.actions 里的所有 action 节点。
 * 默认按 isEdit=true 渲染（可点击 / 可拖拽）；传入 isEdit=false 时
 * 按钮仍可见但禁用（不可点击、不可拖拽、不调用 onAdd），用于查看模式。
 *
 * 分组规则（spec v2 2026-07-23）：
 *   - action.groupLabel 为空字符串 → 不分组展示
 *   - action.groupLabel 非空 → 属于该 label 命名的组
 *   - 组内任一成员 pinned=true → 整个组置顶
 *   - pinned=true 且 groupLabel="" → 单独置顶
 *   - 渲染顺序：置顶组 → 置顶 ungrouped → 普通组 → 普通 ungrouped
 *
 * 搜索与折叠（spec 2026-07-28）：
 *   - 顶部搜索框按 action.label 子串匹配，大小写不敏感（String.includes）
 *   - keyword === "" 走分组视图（divider + 可折叠 + 一键全部折叠/展开）
 *   - keyword !== "" 走扁平视图（仅匹配按钮，无 divider/折叠/toggle-all）
 *   - collapsedLabels: Set<string> 内部 state，key 为 groupLabel 或
 *     "__ungrouped_pinned" / "__ungrouped_normal" 内部约定
 *   - 未分组块用文案"未分组"包裹，可独立折叠
 *   - toggle-all 仅动 collapsedLabels，不影响 keyword / disabled / 派生数据
 *   - isEdit=false 时搜索/折叠仍可用，仅组件按钮 disabled
 *
 * 注意：Python 端通过 model_dump(by_alias=True) 传 schema，
 *       React 端字段名是 camelCase（groupLabel 不是 group_label）。
 *
 * 两种添加方式（同时支持，作为兜底）：
 *   1) HTML5 拖拽：onDragStart 把 action key 写入 dataTransfer (MIME "application/tfe-action")，
 *      FlowCanvas 的 document 级 drop 监听读取并用 screenToFlowPosition 放到准确位置。
 *   2) 点击添加：onClick 直接调用 onAdd（不传 position）；上层取画布视口中心再 addNode。
 */
function ComponentLibrary({ schema, isEdit = true, onAdd }) {
  const [keyword, setKeyword] = useState("");
  const onSearchChange = (e) => setKeyword(e.target.value);
  const onSearchClear = () => setKeyword("");

  const [collapsedLabels, setCollapsedLabels] = useState(() => new Set());
  const toggleCollapse = (label) =>
    setCollapsedLabels((prev) => {
      const next = new Set(prev);
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });

  const matches = (action, kw) =>
    !kw || action.label.toLowerCase().includes(kw.toLowerCase());

  const actions = schema?.actions ?? {};

  // 1. 按 groupLabel 拆分：grouped（非空 label）+ ungrouped（空 label）
  //    兼容两种字段名：camelCase（生产环境 model_dump(by_alias=True) 输出）
  //    和 snake_case（Python 端直接构造或测试 helper 使用）
  const groupedMap = new Map();
  const ungrouped = [];
  for (const [key, action] of Object.entries(actions)) {
    const label = action.groupLabel ?? action.group_label ?? "";
    if (label === "") {
      ungrouped.push([key, action]);
    } else {
      if (!groupedMap.has(label)) groupedMap.set(label, []);
      groupedMap.get(label).push([key, action]);
    }
  }

  // 2. ungrouped 进一步按 pinned 拆分
  const pinnedUngrouped = ungrouped.filter(([, a]) => a.pinned);
  const normalUngrouped = ungrouped.filter(([, a]) => !a.pinned);

  // 3. 组按"是否含 pinned"排序（置顶组在前）
  const groupEntries = Array.from(groupedMap.entries()).map(([label, entries]) => ({
    label,
    entries,
    hasPinned: entries.some(([, a]) => a.pinned),
  }));
  groupEntries.sort((a, b) => {
    if (a.hasPinned && !b.hasPinned) return -1;
    if (!a.hasPinned && b.hasPinned) return 1;
    return 0;
  });

  // toggle-all 作用域:所有用户定义的组 + 未分组块
  const allCollapsibleLabels = (() => {
    const labels = new Set();
    for (const g of groupEntries) labels.add(g.label);
    if (pinnedUngrouped.length > 0) labels.add("__ungrouped_pinned");
    if (normalUngrouped.length > 0) labels.add("__ungrouped_normal");
    return labels;
  })();

  const allExpanded =
    allCollapsibleLabels.size > 0 &&
    Array.from(allCollapsibleLabels).every((l) => !collapsedLabels.has(l));

  const onToggleAll = () => {
    setCollapsedLabels(allExpanded ? allCollapsibleLabels : new Set());
  };

  const renderButton = ([key, action]) => (
    <button
      className={`tfe-comp-btn${isEdit ? "" : " tfe-comp-btn-disabled"}`}
      key={key}
      type="button"
      draggable={isEdit}
      disabled={!isEdit}
      // 兜底:用 inline style 强制 width:100%,避免任何 CSS 缓存 / button 元素
      // min-width:auto 导致宽度被压到只剩文字宽度(spec 2026-07-23 反馈根因)。
      style={{ width: "100%", boxSizing: "border-box" }}
      onClick={() => {
        if (!isEdit) return;
        onAdd && onAdd(key);
      }}
      onDragStart={(e) => {
        if (!isEdit) return;
        e.dataTransfer.setData("application/tfe-action", key);
        e.dataTransfer.setData("text/plain", key); // 部分浏览器只认 text/plain
        e.dataTransfer.effectAllowed = "move";
      }}
    >
      <div
        style={{
          width: "26px",
          height: "26px",
          background: action.bg,
          color: action.color,
          borderRadius: "6px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "14px",
        }}
      >
        {action.icon}
      </div>
      {action.label}
    </button>
  );

  const renderGroup = (group) => {
    const isCollapsed = collapsedLabels.has(group.label);
    return (
      <div
        key={group.label}
        className={`tfe-comp-group${isCollapsed ? " tfe-comp-group-collapsed" : ""}`}
        style={{ width: "100%", boxSizing: "border-box" }}
      >
        <button
          type="button"
          className="tfe-comp-divider"
          onClick={() => toggleCollapse(group.label)}
          aria-expanded={!isCollapsed}
        >
          <span className="tfe-comp-divider-caret" aria-hidden="true">▾</span>
          {group.label}
        </button>
        <div className="tfe-comp-group-body">
          {group.entries.map(renderButton)}
        </div>
      </div>
    );
  };

  const pinnedGroups = groupEntries.filter((g) => g.hasPinned);
  const normalGroups = groupEntries.filter((g) => !g.hasPinned);

  // 是否在"分组总块"与"未分组总块"之间画一条分隔线：
  //   - 当 schema 同时存在 group 和 ungrouped 时画线（满足多数用户场景）
  //   - 与 section(pinned/normal) 内部的 group↔ungrouped 边界无关，
  //     避免"只有 1 个 group 时跨 section 不画线"的视觉断层
  // 视觉布局：[所有 groups(置顶在前) → 分隔线 → 所有 ungroupeds(置顶在前)]
  const totalGroups = pinnedGroups.length + normalGroups.length;
  const totalUngrouped = pinnedUngrouped.length + normalUngrouped.length;
  const drawCrossDivider = totalGroups > 0 && totalUngrouped > 0;

  const renderGrouped = () => (
    <>
      {pinnedGroups.map(renderGroup)}
      {normalGroups.map(renderGroup)}
      {drawCrossDivider && (
        <div
          className="tfe-comp-section-divider"
          aria-hidden="true"
          style={{ width: "100%", boxSizing: "border-box" }}
        />
      )}
      {pinnedUngrouped.length > 0 && renderUngroupedBlock("__ungrouped_pinned", pinnedUngrouped)}
      {normalUngrouped.length > 0 && renderUngroupedBlock("__ungrouped_normal", normalUngrouped)}
    </>
  );

  const renderUngroupedBlock = (key, entries) => {
    const isCollapsed = collapsedLabels.has(key);
    return (
      <div
        key={key}
        className={`tfe-comp-group${isCollapsed ? " tfe-comp-group-collapsed" : ""}`}
        style={{ width: "100%", boxSizing: "border-box" }}
      >
        <button
          type="button"
          className="tfe-comp-divider"
          onClick={() => toggleCollapse(key)}
          aria-expanded={!isCollapsed}
        >
          <span className="tfe-comp-divider-caret" aria-hidden="true">▾</span>
          未分组
        </button>
        <div className="tfe-comp-group-body">
          {entries.map(renderButton)}
        </div>
      </div>
    );
  };

  const matchedActions = keyword !== ""
    ? Object.entries(actions).filter(([, a]) => matches(a, keyword))
    : null;

  return (
    <div className="tfe-comp-list">
      <div className="tfe-comp-search">
        <span className="tfe-comp-search-icon" aria-hidden="true">🔍</span>
        <input
          type="search"
          className="tfe-comp-search-input"
          placeholder="搜索组件"
          value={keyword}
          onChange={onSearchChange}
          aria-label="搜索组件"
        />
        {keyword === "" && (
          <button
            type="button"
            className="tfe-comp-toggle-all"
            onClick={onToggleAll}
            aria-label={allExpanded ? "全部折叠" : "全部展开"}
            title={allExpanded ? "全部折叠" : "全部展开"}
            disabled={allCollapsibleLabels.size === 0}
          >
            {allExpanded ? "全部折叠" : "全部展开"}
          </button>
        )}
        {keyword !== "" && (
          <button
            type="button"
            className="tfe-comp-search-clear"
            onClick={onSearchClear}
            aria-label="清除搜索"
          >×</button>
        )}
      </div>
      {matchedActions === null
        ? renderGrouped()
        : matchedActions.length > 0
          ? matchedActions.map(renderButton)
          : <div className="tfe-comp-empty">没有匹配的组件</div>}
    </div>
  );
}

ComponentLibrary.propTypes = {
  schema: PropTypes.object.isRequired,
  /** 是否允许编辑（true=完整编辑，组件按钮可点击/可拖拽；false=只读查看，按钮 disabled 但仍可见）。默认 true。 */
  isEdit: PropTypes.bool,
  onAdd: PropTypes.func,
};

export default ComponentLibrary;